import { type ReactNode } from "react";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { NavLink } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { stronomModules } from "@/data/stronom-modules";

const upcomingHighlights: Partial<Record<"build" | "motion" | "compliance" | "media" | "gov" | "agents", string[]>> = {
  build: [
    "Digital-to-physical translation for complex engineering projects",
    "Safety, sustainability, and compliance gates baked into every milestone",
    "Manufacturing telemetry for audit-ready delivery",
  ],
  motion: [
    "Command authorization for robotics, drones, and autonomous fleets",
    "Simulation-backed validation with deterministic safety corridors",
    "Observability for mission-critical, real-time operations",
  ],
  compliance: [
    "Regulatory templates spanning healthcare, finance, and legal",
    "Continuous monitoring with policy-as-code enforcement",
    "Zero-trust evidence stores for auditors and regulators",
  ],
  media: [
    "Brand safety enforcement across creative automation workflows",
    "Rights management with provenance tracking",
    "Feedback loops for marketing performance without policy drift",
  ],
  gov: [
    "Public service orchestration with transparent logic layers",
    "Policy compliance and accountability from draft to deployment",
    "Citizen-facing audit logs and service guarantees",
  ],
  agents: [
    "Delegation frameworks for enterprise AI assistants",
    "Role-aware safety rails with immutable instruction history",
    "Continuous evaluation for hallucination prevention and security",
  ],
};

interface ModulePlaceholderProps {
  moduleKey: "build" | "motion" | "compliance" | "media" | "gov" | "agents";
  lead?: ReactNode;
}

export const ModulePlaceholder = ({ moduleKey, lead }: ModulePlaceholderProps) => {
  const module = stronomModules.find((item) => item.key === moduleKey);

  if (!module) {
    throw new Error(`Module ${moduleKey} not found in STRONOM Suite`);
  }

  const accentColor = `hsl(var(${module.accentVar}))`;
  const highlights = upcomingHighlights[moduleKey] ?? [];

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[380px] w-[380px] -translate-x-1/2 rounded-full" style={{ background: accentColor, filter: "blur(180px)", opacity: 0.35 }} />
      <div className="pointer-events-none absolute -bottom-48 right-[-10%] h-[360px] w-[360px] rounded-full" style={{ background: accentColor, filter: "blur(220px)", opacity: 0.28 }} />

      <section className="relative px-6 pb-24 pt-20">
        <div className="mx-auto max-w-5xl">
          <Button variant="ghost" className="mb-10 px-0 text-sm text-muted-foreground hover:text-foreground" asChild>
            <NavLink to="/">
              <ArrowLeft className="mr-2 size-4" /> Back to Suite
            </NavLink>
          </Button>

          <Badge variant="outline" className="border-border/60 bg-background/80 text-muted-foreground">
            {module.statusLabel}
          </Badge>
          <h1 className="mt-4 font-display text-4xl font-semibold text-foreground">{module.title}</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{lead ?? module.description}</p>

          <div className="mt-10 grid gap-6 rounded-3xl border border-border/60 bg-card/80 p-8 backdrop-blur md:grid-cols-2">
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                <Clock className="size-4" />
                In Development
              </div>
              <p>
                This module is in structured rollout. Strategic partners receive early access for validation,
                compliance review, and connector planning before public launch.
              </p>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                <ArrowRight className="size-4" />
                Focus Areas
              </div>
              <ul className="space-y-2">
                {highlights.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full" style={{ background: accentColor }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild className="shadow-[0_18px_60px_rgba(0,179,255,0.35)]">
              <a href="mailto:contact@stronom.com?subject=STRONOM%20Suite%20Access">
                Request Early Access
              </a>
            </Button>
            <Button variant="outline" className="border-border/60" asChild>
              <a href="#suite">View all modules</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
